"use client";

import { useEffect, type KeyboardEvent as ReactKeyboardEvent } from "react";

import { useLomdaV3Editor } from "../store/useLomdaV3Editor";
import { isTypingTarget } from "../lib/isTypingTarget";
import { redo, undo } from "./useUndoRedo";

// A non-Latin layout reports Ctrl+Z as e.g. "ז" or "я"; fall back to the
// physical key then, but trust a Latin `key` so QWERTZ's Z stays Z.
const shortcutKey = (e: KeyboardEvent) => {
    if (/^[a-z]$/i.test(e.key)) return e.key.toLowerCase();
    return /^Key[A-Z]$/.test(e.code) ? e.code.slice(3).toLowerCase() : "";
};

const textToolbarKeys = new WeakSet<Event>();

/** On a text toolbar's root: its keys, also from its portalled popovers
 *  (React events bubble through portals), stay with the text. */
export const keepKeysInText = (e: ReactKeyboardEvent) => {
    textToolbarKeys.add(e.nativeEvent);
};

// A popover is role="dialog" too, but doesn't hide the page behind it.
const inModal = (el: EventTarget | null) => {
    const box =
        el instanceof Element &&
        el.closest('[role="dialog"], [role="alertdialog"]');
    return !!box && !box.closest("[data-radix-popper-content-wrapper]");
};

function onKey(e: KeyboardEvent) {
    if (!(e.ctrlKey || e.metaKey)) return;
    if (
        isTypingTarget(e.target) ||
        textToolbarKeys.has(e) ||
        inModal(e.target)
    )
        return;
    const s = useLomdaV3Editor.getState();
    if (s.mode !== "edit" || s.editingBlockId) return;
    const key = shortcutKey(e);

    if (key === "z" || key === "y") {
        e.preventDefault();
        if (key === "y" || e.shiftKey) redo();
        else undo();
    } else if (key === "c" && s.selectedBlockId) {
        e.preventDefault();
        s.copyBlock(s.selectedBlockId);
    } else if (key === "x" && s.selectedBlockId) {
        e.preventDefault();
        s.copyBlock(s.selectedBlockId);
        s.deleteBlock(s.selectedBlockId);
    } else if (key === "v" && s.clipboardBlock) {
        e.preventDefault();
        s.pasteBlock();
    }
}

/** Block-level shortcuts: copy / cut / paste a block, and undo / redo
 *  (Ctrl+Z, Ctrl+Shift+Z, Ctrl+Y). Where focus is decides: a text, its
 *  toolbar or a modal keeps its own keys. */
export function useEditorShortcuts() {
    useEffect(() => {
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);
}
